<template>
  <div class="terminal-management">
    <div v-if="isAdmin" class="card shadow-sm mb-4">
      <div class="card-body">
        <h5 class="card-title">Add New Terminal</h5>
        <p class="text-muted mb-3">Admins can add and delete terminals. Enter the terminal name below.</p>
        <div class="input-group mb-3">
          <input type="text" class="form-control" placeholder="Enter terminal name" v-model="newTerminalName" aria-label="New Terminal Name">
          <button type="button" class="btn btn-primary" @click="addTerminal">Add Terminal</button>
        </div>
      </div>
    </div>

    <div v-if="unregisteredTerminals.length" class="card shadow-sm mb-4">
      <div class="card-body">
        <h5 class="card-title">Unregistered Terminals</h5>
        <p class="text-muted mb-3">Use the PIN code to register these terminals on your kiosk.</p>
        <ul class="list-group list-group-flush">
          <li v-for="terminal in unregisteredTerminals" :key="terminal.id" class="list-group-item d-flex justify-content-between align-items-center">
            <div>
              <strong>{{ terminal.name }}</strong>
              <span class="badge bg-secondary ms-2" v-if="terminal.pin">PIN CODE: {{ terminal.pin }}</span>
              <span class="badge bg-secondary ms-2" v-else>Awaiting Registration</span>
            </div>
            <button class="btn btn-ouline-danger btn-sm" @click="confirmDelete(terminal, false)"><SvgIcon name="delete" /></button>
          </li>
        </ul>
      </div>
    </div>

    <div class="card shadow-sm">
      <div class="card-body">
        <h5 class="card-title">Registered Terminals</h5>
        <ul class="list-group list-group-flush">
          <li v-for="terminal in registeredTerminals" :key="terminal.id" class="list-group-item d-flex justify-content-between align-items-center">
            <div>
              <span :class="['terminal-status', terminal.connected ? 'connected' : 'disconnected']"></span>
              <strong>{{ terminal.name }}</strong>
            </div>
            <div class="d-flex align-items-center">
              <span class="version-pill">Version: {{ terminal.version }}</span>
              <span class="last-seen-pill">{{ formatLastSeen(terminal.last_called_at) }}</span>
              <button class="btn btn-outline-primary btn-sm ms-2" @click="editTerminal(terminal)">
                <SvgIcon name="edit" />
              </button>
              <button class="btn btn-outline-danger btn-sm ms-2" @click="confirmDelete(terminal, true)" v-if="isAdmin">
                <SvgIcon name="delete" />
              </button>
            </div>
          </li>
        </ul>
      </div>
    </div>

    <div class="modal fade" id="terminalConfigModal" tabindex="-1" ref="configModal">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Configure Terminal: {{ selectedTerminal?.name }}</h5>
            <button type="button" class="btn-close" @click="closeModal"></button>
          </div>
          <div class="modal-body">
            
            <!-- Global Schedules Section -->
            <div class="config-section mb-4">
              <h6 class="section-title">Global Schedules</h6>
              <p class="section-description">Default schedule availability for all order flows</p>
              <div class="option-group">
                <div class="form-check main-option">
                  <input class="form-check-input" type="checkbox" v-model="allSchedules" @change="toggleAllSchedules" id="all-schedules">
                  <label class="form-check-label fw-bold" for="all-schedules">All Schedules</label>
                </div>
                <div v-if="!allSchedules" class="sub-options">
                  <div class="form-check" v-for="schedule in availableSchedules" :key="schedule.ref">
                    <input class="form-check-input" type="checkbox" 
                      :value="schedule.ref" 
                      v-model="selectedSchedules"
                      :id="'schedule-' + schedule.ref">
                    <label class="form-check-label" :for="'schedule-' + schedule.ref">
                      {{ schedule.name }}
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <!-- Order Flows Section -->
            <div class="config-section mb-4">
              <h6 class="section-title">Order Flows</h6>
              <p class="section-description">Choose which order types this terminal can accept</p>
              <div class="option-group">
                <div class="form-check main-option">
                  <input class="form-check-input" type="checkbox" v-model="allOrderFlows" @change="toggleAllOrderFlows" id="all-flows">
                  <label class="form-check-label fw-bold" for="all-flows">All Order Flows</label>
                </div>
                <div v-if="!allOrderFlows" class="sub-options">
                  <div class="form-check" v-for="flow in availableOrderFlows" :key="flow.id">
                    <input class="form-check-input" type="checkbox" 
                      :value="flow.id" 
                      v-model="selectedOrderFlows"
                      :id="'flow-' + flow.id">
                    <label class="form-check-label" :for="'flow-' + flow.id">
                      {{ flow.name }}
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <!-- Per-Order Flow Overrides Section -->
            <div class="config-section" v-if="selectedOrderFlows.length">
              <h6 class="section-title">Schedule Overrides by Order Flow</h6>
              <p class="section-description">Override schedules for specific order flows (optional)</p>
              
              <div v-for="flow in selectedOrderFlows" :key="flow" class="flow-override mb-3">
                <div class="flow-header">
                  <strong class="flow-name">{{ availableOrderFlows.find(f => f.id === flow)?.name || flow }}</strong>
                </div>
                <div class="option-group">
                  <div class="form-check main-option">
                    <input class="form-check-input" type="checkbox" 
                           :id="'all-sched-' + flow" 
                           v-model="allOrderFlowSchedules[flow]" 
                           @change="toggleAllOrderFlowSchedules(flow)">
                    <label class="form-check-label" :for="'all-sched-' + flow">Use global schedules</label>
                  </div>
                  <div v-if="!allOrderFlowSchedules[flow]" class="sub-options">
                    <div class="form-check" v-for="schedule in availableSchedules" :key="'sched-' + flow + '-' + schedule.ref">
                      <input class="form-check-input" type="checkbox"
                        :value="schedule.ref"
                        :checked="isScheduleSelectedForFlow(flow, schedule.ref)"
                        @change="toggleScheduleForFlow(flow, schedule.ref)"
                        :id="'sched-' + flow + '-' + schedule.ref">
                      <label class="form-check-label" :for="'sched-' + flow + '-' + schedule.ref">
                        {{ schedule.name }}
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeModal">Cancel</button>
            <button type="button" class="btn btn-primary" @click="saveTerminalConfig">Save Changes</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { Modal } from 'bootstrap';

export default {
  data() {
    return {
      newTerminalName: '',
      selectedTerminal: null,
      selectedOrderFlows: [],
      selectedSchedules: [],
      allOrderFlows: true,
      allSchedules: true,
      configModal: null,
      selectedOrderFlowSchedules: {},
      allOrderFlowSchedules: {},
    };
  },
  mounted() {
    this.$store.dispatch('admin/fetchTerminals');
    this.configModal = new Modal(this.$refs.configModal);
  },
  computed: {
    ...mapGetters("admin", ['isAdmin']),
    unregisteredTerminals() {
      return this.terminals?.filter(t => !t.registered) || [];
    },
    registeredTerminals() {
      return this.terminals?.filter(t => t.registered) || [];
    },
    terminals() {
      return this.$store.state.admin.terminals;
    },
    availableOrderFlows() {
      return this.$store.state.admin.config?.orderTypes || [];
    },
    availableSchedules() {
      return this.$store.state.admin.schedules || [];
    },
  },
  methods: {
    addTerminal() {
      if (this.newTerminalName.trim()) {
        const terminalData = {
          name: this.newTerminalName,
          locationId: this.$store.state.admin.activeLocation
        };
        this.$store.dispatch('admin/addTerminal', terminalData);
        this.newTerminalName = ''; // Clear the input after adding
      }
    },
    confirmDelete(terminal, isRegistered) {
      const message = isRegistered
        ? "Are you sure? This will unlink and reset the terminal. You will need to re-register on premise."
        : "Are you sure you want to delete this terminal?";
      if (confirm(message)) {
        this.$store.dispatch('admin/deleteTerminal', terminal.id);
      }
    },
    saveTerminals() {
      this.$store.dispatch('admin/fetchTerminals');
    },
    formatLastSeen(lastSeenDate) {
      const now = new Date();
      const lastSeen = new Date(lastSeenDate);
      const diffTime = Math.abs(now - lastSeen);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDays <= 1) {
        return "Today";
      }
      return `${diffDays} days ago`;
    },
    editTerminal(terminal) {
      this.selectedTerminal = terminal;
      this.selectedOrderFlows = terminal.config?.orderFlows || [];
      this.selectedSchedules = terminal.config?.schedules || [];
      this.allOrderFlows = this.selectedOrderFlows.length === 0;
      this.allSchedules = this.selectedSchedules.length === 0;
      // Per-order-flow schedules - re-initialize reactively
      const initialFlowSchedules = terminal.config?.orderFlowSchedules || {};
      this.selectedOrderFlowSchedules = {}; // Reset
      this.allOrderFlowSchedules = {}; // Reset
      (this.selectedOrderFlows || []).forEach(flow => {
        // Deep copy schedule array to prevent reference sharing
        const schedules = initialFlowSchedules[flow] ? [...initialFlowSchedules[flow]] : [];
        this.selectedOrderFlowSchedules[flow] = schedules;
        // Set the 'All' checkbox state reactively
        this.allOrderFlowSchedules[flow] = schedules.length === 0;
      });
      this.configModal.show();
    },
    closeModal() {
      this.configModal.hide();
      this.selectedTerminal = null;
      this.selectedOrderFlows = [];
      this.selectedSchedules = [];
      this.selectedOrderFlowSchedules = {};
      this.allOrderFlowSchedules = {};
    },
    async saveTerminalConfig() {
      if (!this.selectedTerminal) return;
      
      const orderFlowSchedules = {};
      (this.selectedOrderFlows || []).forEach(flow => {
        if (!this.allOrderFlowSchedules[flow] && this.selectedOrderFlowSchedules[flow] && this.selectedOrderFlowSchedules[flow].length > 0) {
          orderFlowSchedules[flow] = this.selectedOrderFlowSchedules[flow];
        }
      });
      const config = {
        orderFlows: this.allOrderFlows ? null : this.selectedOrderFlows,
        schedules: this.allSchedules ? null : this.selectedSchedules,
        orderFlowSchedules: Object.keys(orderFlowSchedules).length > 0 ? orderFlowSchedules : undefined
      };
      
      try {
        await this.$store.dispatch('admin/updateTerminalConfig', {
          terminalId: this.selectedTerminal.id,
          config
        });
        this.closeModal();
        this.$store.dispatch('admin/fetchTerminals');
      } catch (error) {
        console.error('Failed to update terminal config:', error);
      }
    },
    toggleAllOrderFlows() {
      if (this.allOrderFlows) {
        this.selectedOrderFlows = [];
      }
    },
    toggleAllSchedules() {
      if (this.allSchedules) {
        this.selectedSchedules = [];
      }
    },
    toggleAllOrderFlowSchedules(flow) {
      if (this.allOrderFlowSchedules[flow]) {
        this.selectedOrderFlowSchedules[flow] = [];
      }
    },
    isScheduleSelectedForFlow(flowId, scheduleRef) {
      return this.selectedOrderFlowSchedules[flowId]?.includes(scheduleRef);
    },
    toggleScheduleForFlow(flowId, scheduleRef) {
      const schedules = this.selectedOrderFlowSchedules[flowId];
      if (!schedules) return;

      const index = schedules.indexOf(scheduleRef);
      if (index > -1) {
        schedules.splice(index, 1);
      } else {
        schedules.push(scheduleRef);
      }
    }
  }
};
</script>

<style scoped>

.card {
  box-shadow: none !important;
}

.input-group .form-control {
  border-radius: 0.25rem;
}

.list-group-item {
  padding-left: 0 !important;
  padding-right: 0 !important;
}

.terminal-status {
  height: 10px;
  width: 10px;
  border-radius: 50%;
  display: inline-block;
  margin-right: 5px;
}

.connected {
  background-color: green;
}

.disconnected {
  background-color: grey;
}

.card {
  border-radius: 0.5rem;
}

.card-title {
  font-weight: bold;
  margin-bottom: 0.5rem;
}

.text-muted {
  font-size: 0.9rem;
}

.badge {
  font-size: 1rem;
  padding: 0.5em;
}

.version-pill {
  background-color: #6c757d;
  color: white;
  border-radius: 50px;
  padding: 0.25em 0.75em;
  font-size: 0.9rem;
  margin-right: 10px;
  display: inline-block;
}

.last-seen-pill {
  background-color: #adb5bd;
  color: white;
  border-radius: 50px;
  padding: 0.25em 0.75em;
  font-size: 0.9rem;
  margin-right: 10px;
  display: inline-block;
}

.modal-dialog {
  max-width: 600px;
}

.config-section {
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 1.25rem;
  background-color: #f8f9fa;
}

.section-title {
  color: #495057;
  font-weight: 600;
  margin-bottom: 0.5rem;
  font-size: 1.1rem;
}

.section-description {
  color: #6c757d;
  font-size: 0.9rem;
  margin-bottom: 1rem;
}

.option-group {
  background-color: white;
  border-radius: 6px;
  padding: 1rem;
}

.main-option {
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #e9ecef;
  margin-bottom: 0.75rem;
}

.main-option:last-child {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

.sub-options {
  padding-left: 1.5rem;
}

.sub-options .form-check {
  margin-bottom: 0.5rem;
}

.flow-override {
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 1rem;
  background-color: white;
}

.flow-header {
  margin-bottom: 0.75rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #e9ecef;
}

.flow-name {
  color: #495057;
  font-size: 1rem;
}

.form-check {
  margin-bottom: 0.5rem;
}

.form-check:last-child {
  margin-bottom: 0;
}

.form-label {
  font-weight: bold;
  margin-bottom: 0.5rem;
}

</style>
